'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DateCalculator() {
  const t = useTranslations("tools.dateCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentMode, setCurrentMode] = useState<'difference' | 'add' | 'subtract'>('difference');

  // Difference mode state
  const [startDate, setStartDate] = useState({
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
    year: new Date().getFullYear()
  });
  const [endDate, setEndDate] = useState({
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
    year: new Date().getFullYear()
  });
  const [includeEndDay, setIncludeEndDay] = useState(false);
  const [excludeWeekends, setExcludeWeekends] = useState(false);

  // Add/Subtract mode state
  const [addSubDate, setAddSubDate] = useState({
    month: new Date().getMonth() + 1,
    day: new Date().getDate(),
    year: new Date().getFullYear()
  });
  const [numberValue, setNumberValue] = useState(1);
  const [unitValue, setUnitValue] = useState<'days' | 'weeks' | 'months' | 'years'>('days');

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, margin: '0 0 0.5rem 0' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    modeButtons: getResponsiveStyle(
      { display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      { marginBottom: '2rem' }
    ),
    modeBtn: getResponsiveStyle(
      {
        flex: 1,
        padding: '1rem',
        border: '2px solid #cbd5e1',
        background: 'white',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontWeight: 600,
        color: '#475569',
        minWidth: '200px'
      },
      { minWidth: '100%', fontSize: '0.95rem' },
      { fontSize: '1rem' },
      {}
    ),
    modeBtnActive: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderColor: '#2563eb',
    },
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        marginBottom: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2rem', marginBottom: '2rem' }
    ),
    formGroup: getResponsiveStyle(
      { marginBottom: '1.25rem' },
      {},
      { marginBottom: '1.5rem' },
      {}
    ),
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#334155' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    dateInputWrapper: getResponsiveStyle(
      { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' as const },
      { flexDirection: 'column' as const },
      { flexDirection: 'row' as const },
      {}
    ),
    select: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        flex: 1,
        minWidth: '100px',
        fontSize: '1rem'
      },
      { width: '100%' },
      {},
      {}
    ),
    input: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        transition: 'all 0.3s ease',
        fontSize: '1rem'
      },
      { width: '100%', flex: '0 0 auto' },
      { flex: '0 0 80px' },
      {}
    ),
    checkboxGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '1rem'
    },
    checkbox: {
      width: '18px',
      height: '18px',
      cursor: 'pointer'
    },
    checkboxLabel: {
      margin: 0,
      cursor: 'pointer',
      fontWeight: 500,
      fontSize: '0.95rem'
    },
    numberInputGroup: getResponsiveStyle(
      { display: 'flex', gap: '1rem', alignItems: 'end' },
      { flexDirection: 'column' as const, alignItems: 'stretch' as const },
      { flexDirection: 'row' as const },
      {}
    ),
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1.5rem'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' }
    ),
    resultsCard: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { margin: '0 0 1.5rem 0', color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultGrid: getResponsiveStyle(
      { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
    ),
    resultCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        padding: '1.25rem',
        borderRadius: '0.75rem',
        border: '2px solid #bae6fd'
      },
      {},
      {},
      {}
    ),
    resultLabel: {
      fontSize: '0.85rem',
      color: '#475569',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      marginBottom: '0.5rem'
    },
    resultValue: getResponsiveStyle(
      { color: '#2563eb', fontWeight: 700 },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '1.75rem' }
    ),
    resultDetail: getResponsiveStyle(
      {
        background: '#f8fafc',
        padding: '1.5rem',
        borderRadius: '0.75rem',
        borderLeft: '4px solid #f59e0b'
      },
      {},
      {},
      {}
    ),
    detailLabel: {
      fontWeight: 600,
      color: '#334155',
      marginBottom: '0.5rem',
      fontSize: '0.95rem'
    },
    detailValue: getResponsiveStyle(
      { color: colors.textPrimary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.1rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem', marginBottom: '2rem' },
      { padding: '2.5rem', borderRadius: '1.5rem', marginBottom: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqItem: getResponsiveStyle(
      { marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      {}
    ),
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.2rem' },
      { fontSize: '1.3rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    refLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
      fontSize: '0.95rem'
    }
  };

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const calculateDifference = (start: Date, end: Date, includeEnd: boolean, excludeWeekends: boolean) => {
    let startCopy = new Date(start);
    let endCopy = new Date(end);

    if (startCopy > endCopy) {
      [startCopy, endCopy] = [endCopy, startCopy];
    }

    const totalMs = endCopy.getTime() - startCopy.getTime();
    let totalDays = Math.floor(totalMs / (1000 * 60 * 60 * 24));

    if (includeEnd) {
      totalDays += 1;
    }

    let businessDays = totalDays;
    if (excludeWeekends) {
      businessDays = 0;
      let current = new Date(startCopy);
      const endTime = endCopy.getTime();

      while (current.getTime() <= endTime) {
        if (!isWeekend(current)) {
          businessDays++;
        }
        current.setDate(current.getDate() + 1);
      }
    }

    const years = Math.floor(totalDays / 365.25);
    const months = Math.floor(totalDays / 30.44);
    const weeks = Math.floor(totalDays / 7);

    return {
      totalDays: excludeWeekends ? businessDays : totalDays,
      years,
      months,
      weeks,
      excludeWeekends,
      startDate: startCopy,
      endDate: endCopy
    };
  };

  const addToDate = (start: Date, number: number, unit: string) => {
    const result = new Date(start);

    switch(unit) {
      case 'days':
        result.setDate(result.getDate() + number);
        break;
      case 'weeks':
        result.setDate(result.getDate() + (number * 7));
        break;
      case 'months':
        result.setMonth(result.getMonth() + number);
        break;
      case 'years':
        result.setFullYear(result.getFullYear() + number);
        break;
    }

    return result;
  };

  const subtractFromDate = (start: Date, number: number, unit: string) => {
    const result = new Date(start);

    switch(unit) {
      case 'days':
        result.setDate(result.getDate() - number);
        break;
      case 'weeks':
        result.setDate(result.getDate() - (number * 7));
        break;
      case 'months':
        result.setMonth(result.getMonth() - number);
        break;
      case 'years':
        result.setFullYear(result.getFullYear() - number);
        break;
    }

    return result;
  };

  const formatDate = (date: Date) => {
    const months = [
      t('january'), t('february'), t('march'), t('april'), t('may'), t('june'),
      t('july'), t('august'), t('september'), t('october'), t('november'), t('december')
    ];
    const days = [
      t('sunday'), t('monday'), t('tuesday'), t('wednesday'),
      t('thursday'), t('friday'), t('saturday')
    ];

    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (currentMode === 'difference') {
      const start = new Date(startDate.year, startDate.month - 1, startDate.day);
      const end = new Date(endDate.year, endDate.month - 1, endDate.day);
      const diff = calculateDifference(start, end, includeEndDay, excludeWeekends);
      setResults({ mode: 'difference', data: diff });
    } else if (currentMode === 'add') {
      const start = new Date(addSubDate.year, addSubDate.month - 1, addSubDate.day);
      const result = addToDate(start, numberValue, unitValue);
      setResults({ mode: 'add', startDate: start, resultDate: result, number: numberValue, unit: unitValue });
    } else if (currentMode === 'subtract') {
      const start = new Date(addSubDate.year, addSubDate.month - 1, addSubDate.day);
      const result = subtractFromDate(start, numberValue, unitValue);
      setResults({ mode: 'subtract', startDate: start, resultDate: result, number: numberValue, unit: unitValue });
    }
  };

  const monthOptions = [
    { value: 1, label: t('january') },
    { value: 2, label: t('february') },
    { value: 3, label: t('march') },
    { value: 4, label: t('april') },
    { value: 5, label: t('may') },
    { value: 6, label: t('june') },
    { value: 7, label: t('july') },
    { value: 8, label: t('august') },
    { value: 9, label: t('september') },
    { value: 10, label: t('october') },
    { value: 11, label: t('november') },
    { value: 12, label: t('december') }
  ];

  const unitOptions = [
    { value: 'days', label: t('days') },
    { value: 'weeks', label: t('weeks') },
    { value: 'months', label: t('months') },
    { value: 'years', label: t('years') }
  ];

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t('toolTitle')}</h2>
          </div>

          <div style={styles.modeButtons}>
            <button
              type="button"
              style={{
                ...styles.modeBtn,
                ...(currentMode === 'difference' ? styles.modeBtnActive : {})
              }}
              onClick={() => setCurrentMode('difference')}
              onMouseEnter={(e) => {
                if (currentMode !== 'difference') {
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentMode !== 'difference') {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              📊 {t('modeDifference')}
            </button>
            <button
              type="button"
              style={{
                ...styles.modeBtn,
                ...(currentMode === 'add' ? styles.modeBtnActive : {})
              }}
              onClick={() => setCurrentMode('add')}
              onMouseEnter={(e) => {
                if (currentMode !== 'add') {
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentMode !== 'add') {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              ➕ {t('modeAdd')}
            </button>
            <button
              type="button"
              style={{
                ...styles.modeBtn,
                ...(currentMode === 'subtract' ? styles.modeBtnActive : {})
              }}
              onClick={() => setCurrentMode('subtract')}
              onMouseEnter={(e) => {
                if (currentMode !== 'subtract') {
                  e.currentTarget.style.borderColor = '#2563eb';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentMode !== 'subtract') {
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              ➖ {t('modeSubtract')}
            </button>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            {currentMode === 'difference' && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('startDateLabel')}</label>
                  <div style={styles.dateInputWrapper}>
                    <select
                      style={styles.select}
                      value={startDate.month}
                      onChange={(e) => setStartDate({...startDate, month: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    >
                      <option value="">{t('monthPlaceholder')}</option>
                      {monthOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      style={styles.input}
                      placeholder={t('dayPlaceholder')}
                      min="1"
                      max="31"
                      value={startDate.day}
                      onChange={(e) => setStartDate({...startDate, day: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <input
                      type="number"
                      style={styles.input}
                      placeholder={t('yearPlaceholder')}
                      min="1900"
                      max="2100"
                      value={startDate.year}
                      onChange={(e) => setStartDate({...startDate, year: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('endDateLabel')}</label>
                  <div style={styles.dateInputWrapper}>
                    <select
                      style={styles.select}
                      value={endDate.month}
                      onChange={(e) => setEndDate({...endDate, month: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    >
                      <option value="">{t('monthPlaceholder')}</option>
                      {monthOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      style={styles.input}
                      placeholder={t('dayPlaceholder')}
                      min="1"
                      max="31"
                      value={endDate.day}
                      onChange={(e) => setEndDate({...endDate, day: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <input
                      type="number"
                      style={styles.input}
                      placeholder={t('yearPlaceholder')}
                      min="1900"
                      max="2100"
                      value={endDate.year}
                      onChange={(e) => setEndDate({...endDate, year: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                  </div>
                </div>

                <div style={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="include-end-day"
                    style={styles.checkbox}
                    checked={includeEndDay}
                    onChange={(e) => setIncludeEndDay(e.target.checked)}
                  />
                  <label htmlFor="include-end-day" style={styles.checkboxLabel}>
                    {t('includeEndDay')}
                  </label>
                </div>

                <div style={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="exclude-weekends"
                    style={styles.checkbox}
                    checked={excludeWeekends}
                    onChange={(e) => setExcludeWeekends(e.target.checked)}
                  />
                  <label htmlFor="exclude-weekends" style={styles.checkboxLabel}>
                    {t('excludeWeekends')}
                  </label>
                </div>
              </>
            )}

            {currentMode === 'add' && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('startDateLabel')}</label>
                  <div style={styles.dateInputWrapper}>
                    <select
                      style={styles.select}
                      value={addSubDate.month}
                      onChange={(e) => setAddSubDate({...addSubDate, month: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    >
                      <option value="">{t('monthPlaceholder')}</option>
                      {monthOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      style={styles.input}
                      placeholder={t('dayPlaceholder')}
                      min="1"
                      max="31"
                      value={addSubDate.day}
                      onChange={(e) => setAddSubDate({...addSubDate, day: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <input
                      type="number"
                      style={styles.input}
                      placeholder={t('yearPlaceholder')}
                      min="1900"
                      max="2100"
                      value={addSubDate.year}
                      onChange={(e) => setAddSubDate({...addSubDate, year: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                  </div>
                </div>

                <div style={styles.numberInputGroup}>
                  <div style={{...styles.formGroup, marginBottom: 0, flex: 1}}>
                    <label style={styles.label}>{t('addLabel')}</label>
                    <input
                      type="number"
                      style={{...styles.input, width: '100%'}}
                      min="0"
                      value={numberValue}
                      onChange={(e) => setNumberValue(parseInt(e.target.value))}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                  </div>
                  <div style={{...styles.formGroup, marginBottom: 0, flex: 1}}>
                    <label style={styles.label}>{t('unitLabel')}</label>
                    <select
                      style={{...styles.select, width: '100%'}}
                      value={unitValue}
                      onChange={(e) => setUnitValue(e.target.value as any)}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    >
                      {unitOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {currentMode === 'subtract' && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('startDateLabel')}</label>
                  <div style={styles.dateInputWrapper}>
                    <select
                      style={styles.select}
                      value={addSubDate.month}
                      onChange={(e) => setAddSubDate({...addSubDate, month: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    >
                      <option value="">{t('monthPlaceholder')}</option>
                      {monthOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      style={styles.input}
                      placeholder={t('dayPlaceholder')}
                      min="1"
                      max="31"
                      value={addSubDate.day}
                      onChange={(e) => setAddSubDate({...addSubDate, day: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                    <input
                      type="number"
                      style={styles.input}
                      placeholder={t('yearPlaceholder')}
                      min="1900"
                      max="2100"
                      value={addSubDate.year}
                      onChange={(e) => setAddSubDate({...addSubDate, year: parseInt(e.target.value)})}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                  </div>
                </div>

                <div style={styles.numberInputGroup}>
                  <div style={{...styles.formGroup, marginBottom: 0, flex: 1}}>
                    <label style={styles.label}>{t('subtractLabel')}</label>
                    <input
                      type="number"
                      style={{...styles.input, width: '100%'}}
                      min="0"
                      value={numberValue}
                      onChange={(e) => setNumberValue(parseInt(e.target.value))}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    />
                  </div>
                  <div style={{...styles.formGroup, marginBottom: 0, flex: 1}}>
                    <label style={styles.label}>{t('unitLabel')}</label>
                    <select
                      style={{...styles.select, width: '100%'}}
                      value={unitValue}
                      onChange={(e) => setUnitValue(e.target.value as any)}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      required
                    >
                      {unitOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              style={styles.calculateBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(37, 99, 235, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={styles.resultsCard}>
              <h3 style={styles.resultsTitle}>✨ {t('resultsTitle')}</h3>

              {results.mode === 'difference' && (
                <>
                  <div style={styles.resultGrid}>
                    <div style={styles.resultCard}>
                      <div style={styles.resultLabel}>
                        {results.data.excludeWeekends ? t('businessDaysLabel') : t('totalDaysLabel')}
                      </div>
                      <div style={styles.resultValue}>{results.data.totalDays.toLocaleString()}</div>
                    </div>
                    <div style={styles.resultCard}>
                      <div style={styles.resultLabel}>{t('weeksLabel')}</div>
                      <div style={styles.resultValue}>{results.data.weeks.toLocaleString()}</div>
                    </div>
                    <div style={styles.resultCard}>
                      <div style={styles.resultLabel}>{t('monthsLabel')}</div>
                      <div style={styles.resultValue}>{results.data.months.toLocaleString()}</div>
                    </div>
                    <div style={styles.resultCard}>
                      <div style={styles.resultLabel}>{t('yearsLabel')}</div>
                      <div style={styles.resultValue}>{results.data.years.toLocaleString()}</div>
                    </div>
                  </div>
                  <div style={styles.resultDetail}>
                    <div style={styles.detailLabel}>{t('fromLabel')}</div>
                    <div style={styles.detailValue}>{formatDate(results.data.startDate)}</div>
                    <div style={{...styles.detailLabel, marginTop: '1rem'}}>{t('toLabel')}</div>
                    <div style={styles.detailValue}>{formatDate(results.data.endDate)}</div>
                  </div>
                </>
              )}

              {results.mode === 'add' && (
                <div style={styles.resultDetail}>
                  <div style={styles.detailLabel}>{t('startDateResultLabel')}</div>
                  <div style={styles.detailValue}>{formatDate(results.startDate)}</div>
                  <div style={{...styles.detailLabel, marginTop: '1rem'}}>
                    {t('addingLabel')} {results.number} {t(results.unit)}:
                  </div>
                  <div style={{...styles.detailValue, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', color: '#2563eb', fontWeight: 700, marginTop: '0.5rem'}}>
                    {formatDate(results.resultDate)}
                  </div>
                </div>
              )}

              {results.mode === 'subtract' && (
                <div style={styles.resultDetail}>
                  <div style={styles.detailLabel}>{t('startDateResultLabel')}</div>
                  <div style={styles.detailValue}>{formatDate(results.startDate)}</div>
                  <div style={{...styles.detailLabel, marginTop: '1rem'}}>
                    {t('subtractingLabel')} {results.number} {t(results.unit)}:
                  </div>
                  <div style={{...styles.detailValue, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', color: '#2563eb', fontWeight: 700, marginTop: '0.5rem'}}>
                    {formatDate(results.resultDate)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introPara1')}</p>
        <p style={styles.paragraph}>{t('introPara2')}</p>
        <p style={styles.paragraph}>{t('introPara3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep1')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep2')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep3')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep4')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsPara1')}</p>
        <ul style={styles.list}>
          <li style={{marginBottom: '0.75rem'}}><strong>{t('insightPoint1Title')}</strong> {t('insightPoint1')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong>{t('insightPoint2Title')}</strong> {t('insightPoint2')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong>{t('insightPoint3Title')}</strong> {t('insightPoint3')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong>{t('insightPoint4Title')}</strong> {t('insightPoint4')}</li>
          <li style={{marginBottom: '0.75rem'}}><strong>{t('insightPoint5Title')}</strong> {t('insightPoint5')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <p style={styles.paragraph}>{t('detailsPara1')}</p>
        <p style={styles.paragraph}>{t('detailsPara2')}</p>
        <p style={styles.paragraph}>{t('detailsPara3')}</p>
        <p style={styles.paragraph}>{t('detailsPara4')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://day-off.app/days-calculator-features-benefits-and-use-cases/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Days Calculator: Features, Benefits, and Use Cases - Day Off
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://formidableforms.com/online-date-calculator/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Create an Online Date Calculator - Formidable Forms
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.filevine.com/blog/legal-deadline-calculators-your-tool-for-timely-filing/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Legal Deadline Calculators: Your Tool for Timely Filing - Filevine
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://calculator.ecofriendly-product.com/date-calculator/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Date Calculator – Your Interactive Guide to Managing Dates
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://tradebrains.in/features/why-every-business-should-use-a-date-calculator-for-project-management/" target="_blank" rel="noopener noreferrer" style={styles.refLink}>
              Why Every Business Should Use a Date Calculator for Project Management
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}